import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateUser, logout } from '../store/slices/authSlice';
import api from '../services/api';
import { toast } from 'react-toastify';
import { subscribePush, unsubscribePush, pushNotificationStatus } from '../services/pushNotifications';
import { buildSocialUrl, socialHandle, SOCIAL_PREFIX } from '../utils/socialLinks';
import {
  USERNAME_MAX, USERNAME_HINT, RENAME_COOLDOWN_DAYS,
  sanitiseUsernameInput, validateUsernameFormat, isSameUsername,
} from '../utils/usernameRules';

const AU_STATES = ['', 'NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

// Social handle input: a fixed platform prefix + a borderless field, so people
// only type their username (we build the link on save via utils/socialLinks).
const socialWrapStyle = { display: 'flex', alignItems: 'center', background: '#0f131c', border: '1px solid #2a3142', borderRadius: 8, overflow: 'hidden' };
const socialPrefixStyle = { paddingLeft: 12, color: '#5a657a', fontSize: '0.9rem', whiteSpace: 'nowrap', userSelect: 'none' };
const socialInputStyle = { flex: 1, minWidth: 0, background: 'transparent', border: 'none', color: '#f0f1f5', padding: '10px 12px 10px 2px', fontSize: '0.9rem', outline: 'none' };

// Availability indicators for the username field. Stroked SVG in the Feather
// idiom, matching the icons elsewhere in the client (see
// components/marketplace/MarketplaceFilterSheet.jsx).
const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="#4caf7d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const CrossIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="#e84545" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const SpinnerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="#8892b0" strokeWidth="2.5" strokeLinecap="round"
    style={{ animation: 'spin 0.8s linear infinite' }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// Downscale an avatar in the browser before upload. Profile photos render small,
// so a 512px JPEG is plenty and keeps payloads well under the server's size cap —
// raw phone/camera photos are routinely 5–12 MB and would otherwise 413. Falls
// back to the original File if anything goes wrong (decode failure, odd format).
const AVATAR_MAX_DIM = 512;
const downscaleImage = (file) => new Promise((resolve) => {
  if (!file.type?.startsWith('image/')) return resolve(file);
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, AVATAR_MAX_DIM / Math.max(img.width, img.height));
    if (scale === 1 && file.size <= 1_000_000) return resolve(file); // already small
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return resolve(file);
      resolve(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const Section = ({ title, children, danger }) => (
  <div style={{
    background: '#141821',
    border: `1px solid ${danger ? '#3a1a1a' : '#2a3142'}`,
    borderRadius: 0,
    padding: 24,
  }}>
    {title && <h3 style={{ marginBottom: 20, color: danger ? '#e84545' : '#f0f1f5' }}>{title}</h3>}
    {children}
  </div>
);

const Toggle = ({ checked, onChange, label, description }) => (
  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer' }}>
    <div
      onClick={onChange}
      style={{
        flexShrink: 0,
        marginTop: 2,
        width: 40,
        height: 22,
        borderRadius: 11,
        background: checked ? '#4a9eff' : '#2a3142',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div style={{
        position: 'absolute',
        top: 2,
        left: checked ? 19 : 2,
        width: 16,
        height: 16,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
    <div>
      <div style={{ fontWeight: 600, marginBottom: 3, fontSize: '0.9rem' }}>{label}</div>
      {description && <div style={{ color: '#8892b0', fontSize: '0.82rem', lineHeight: 1.5 }}>{description}</div>}
    </div>
  </label>
);

// ── Feedback constants ────────────────────────────────────────────────────────

const AREAS = [
  { value: '', label: 'Select a section…' },
  { value: 'garage', label: 'My Garage' },
  { value: 'events', label: 'Events' },
  { value: 'profile', label: 'Profile' },
  { value: 'feed', label: 'Feed & Social' },
  { value: 'routes', label: 'Routes & Landmarks' },
  { value: 'messages', label: 'Messages' },
  { value: 'discover', label: 'Search & Discover' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'auth', label: 'Login / Sign Up' },
  { value: 'settings', label: 'Settings' },
  { value: 'performance', label: 'Performance / Speed' },
  { value: 'other', label: 'Other' },
];

const ISSUE_TYPES = [
  { value: '', label: 'Select issue type…' },
  { value: 'bug', label: 'Bug / Error — something is broken' },
  { value: 'visual_glitch', label: 'Visual Glitch — display looks wrong' },
  { value: 'slow_loading', label: 'Slow Loading — performance issue' },
  { value: 'crash', label: 'Crash — app won\'t load or freezes' },
  { value: 'feature_request', label: 'Feature Request — want something new' },
  { value: 'ui_design', label: 'UI / Design — layout or style feedback' },
  { value: 'content', label: 'Content Issue — wrong or missing info' },
  { value: 'account', label: 'Account / Data — issue with my data' },
  { value: 'other', label: 'Other' },
];

const SEVERITIES = [
  { value: 'critical', label: 'Critical', desc: 'App is unusable / data loss' },
  { value: 'high', label: 'High', desc: 'Major feature broken' },
  { value: 'medium', label: 'Medium', desc: 'Annoying but workable' },
  { value: 'low', label: 'Low', desc: 'Minor or cosmetic' },
];

const SEV_COLORS = { critical: '#e84545', high: '#f5a623', medium: '#4a9eff', low: '#2ecc71' };

// ── FeedbackTab component ─────────────────────────────────────────────────────

const FeedbackTab = () => {
  const [inner, setInner] = useState('structured'); // 'structured' | 'quick'
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    area: '', issueType: '', severity: '', title: '', description: '',
  });
  const [quickMsg, setQuickMsg] = useState('');

  const handleField = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const setSeverity = (val) => setForm(f => ({ ...f, severity: val }));

  const submitStructured = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error('Please add a title');
    setSubmitting(true);
    try {
      await api.post('/feedback', {
        type: 'structured',
        area: form.area || null,
        issueType: form.issueType || null,
        severity: form.severity || null,
        title: form.title,
        description: form.description || null,
      });
      setDone(true);
    } catch { toast.error('Failed to submit — please try again'); }
    finally { setSubmitting(false); }
  };

  const submitQuick = async (e) => {
    e.preventDefault();
    if (!quickMsg.trim()) return toast.error('Please write something first');
    setSubmitting(true);
    try {
      await api.post('/feedback', { type: 'quick', message: quickMsg });
      setDone(true);
    } catch { toast.error('Failed to submit — please try again'); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setDone(false);
    setForm({ area: '', issueType: '', severity: '', title: '', description: '' });
    setQuickMsg('');
  };

  const inputStyle = {
    background: '#0d0f14', border: '1px solid #2a3142', color: '#f0f1f5',
    padding: '9px 12px', fontSize: '0.875rem', width: '100%', fontFamily: 'inherit',
    outline: 'none', borderRadius: 0,
  };
  const labelStyle = { fontSize: '0.8rem', color: '#8892b0', marginBottom: 6, display: 'block' };

  if (done) return (
    <Section title="Thank you!">
      <p style={{ color: '#8892b0', fontSize: '0.9rem', marginBottom: 20 }}>
        Your feedback has been submitted. It helps make MyCarCollection better for everyone.
      </p>
      <button className="btn-primary btn-sm" onClick={reset}>Submit another</button>
    </Section>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Inner tab switcher */}
      <div style={{ display: 'flex', borderBottom: '1px solid #2a3142', marginBottom: 24 }}>
        {[
          { key: 'structured', label: 'Report an Issue' },
          { key: 'quick', label: 'Quick Feedback' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => { setInner(t.key); setDone(false); }}
            style={{
              background: 'none', border: 'none', borderBottom: inner === t.key ? '2px solid #4a9eff' : '2px solid transparent',
              color: inner === t.key ? '#f0f1f5' : '#8892b0', fontWeight: inner === t.key ? 600 : 400,
              fontSize: '0.875rem', padding: '8px 18px 10px', cursor: 'pointer', marginBottom: -1,
              transition: 'all 0.15s', fontFamily: 'inherit',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* ── Structured form ── */}
      {inner === 'structured' && (
        <form onSubmit={submitStructured} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Section title="Report an Issue">
            <p style={{ color: '#5a657a', fontSize: '0.8rem', marginBottom: 20 }}>
              Use this form to report bugs, glitches, or leave detailed feedback. The more info the better.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 4 }}>
              <div>
                <label style={labelStyle}>Section of the app</label>
                <select name="area" value={form.area} onChange={handleField} style={inputStyle}>
                  {AREAS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Issue type</label>
                <select name="issueType" value={form.issueType} onChange={handleField} style={inputStyle}>
                  {ISSUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Severity</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {SEVERITIES.map(s => (
                  <button
                    type="button"
                    key={s.value}
                    onClick={() => setSeverity(s.value)}
                    style={{
                      flex: 1, padding: '8px 6px', border: `1px solid ${form.severity === s.value ? SEV_COLORS[s.value] : '#2a3142'}`,
                      background: form.severity === s.value ? `${SEV_COLORS[s.value]}18` : '#0d0f14',
                      color: form.severity === s.value ? SEV_COLORS[s.value] : '#5a657a',
                      cursor: 'pointer', fontFamily: 'inherit', borderRadius: 0, transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.8rem' }}>{s.label}</div>
                    <div style={{ fontSize: '0.68rem', marginTop: 2, opacity: 0.8 }}>{s.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Title <span style={{ color: '#e84545' }}>*</span></label>
              <input
                name="title"
                value={form.title}
                onChange={handleField}
                placeholder="Short summary of the issue"
                style={inputStyle}
                maxLength={200}
                required
              />
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={labelStyle}>Description <span style={{ color: '#5a657a' }}>(optional)</span></label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleField}
                placeholder="Steps to reproduce, what you expected vs what happened, screenshots if possible…"
                rows={5}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              />
            </div>

            <button type="submit" className="btn-primary" style={{ marginTop: 8, alignSelf: 'flex-start' }} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Report'}
            </button>
          </Section>
        </form>
      )}

      {/* ── Quick feedback form ── */}
      {inner === 'quick' && (
        <form onSubmit={submitQuick} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Section title="Quick Feedback">
            <p style={{ color: '#5a657a', fontSize: '0.8rem', marginBottom: 20 }}>
              Just want to say something? Type it here — no categories needed.
            </p>
            <textarea
              value={quickMsg}
              onChange={e => setQuickMsg(e.target.value)}
              placeholder="What's on your mind? Bug, idea, something you love or hate…"
              rows={8}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }}
            />
            <button type="submit" className="btn-primary" style={{ marginTop: 12, alignSelf: 'flex-start' }} disabled={submitting}>
              {submitting ? 'Sending…' : 'Send Feedback'}
            </button>
          </Section>
        </form>
      )}
    </div>
  );
};

// ── NotificationsTab component ────────────────────────────────────────────────

const NOTIF_SECTIONS = [
  {
    title: 'Social',
    items: [
      { key: 'follows',          label: 'New followers',         description: 'When someone follows you or accepts your follow request' },
      { key: 'new_posts',        label: 'Posts from following',  description: 'When someone you follow shares a new post' },
      { key: 'new_events',       label: 'Events from following', description: 'When someone you follow creates an event' },
      { key: 'new_routes',       label: 'Routes from following', description: 'When someone you follow creates a new route' },
    ],
  },
  {
    title: 'Your content',
    items: [
      { key: 'post_likes',       label: 'Likes',            description: 'When someone likes your post' },
      { key: 'post_comments',    label: 'Comments',         description: 'When someone comments on your post' },
      { key: 'event_comments',   label: 'Event comments',   description: 'When someone comments on your event' },
      { key: 'route_reviews',    label: 'Route reviews',    description: 'When someone reviews your route' },
      { key: 'landmark_reviews', label: 'Landmark reviews', description: 'When someone reviews your landmark' },
    ],
  },
  {
    title: 'Messages & events',
    items: [
      { key: 'messages',            label: 'Direct messages',     description: 'New messages in your conversations' },
      { key: 'event_announcements', label: 'Event announcements', description: 'Updates from events you RSVP\'d to' },
    ],
  },
  {
    title: 'From MyCarCollection',
    items: [
      { key: 'announcements', label: 'Product updates', description: 'The rare heads-up when something new lands' },
    ],
  },
];

const NotificationsTab = () => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState(pushNotificationStatus());

  useEffect(() => {
    api.get('/notifications/preferences')
      .then(({ data }) => setPrefs(data.preferences || {}))
      .catch(() => toast.error('Failed to load preferences'))
      .finally(() => setLoading(false));
  }, []);

  const togglePref = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await api.put('/notifications/preferences', { [key]: next[key] });
    } catch {
      setPrefs(prefs);
      toast.error('Failed to update preference');
    }
  };

  const enablePush = async () => {
    const ok = await subscribePush();
    setPermission(pushNotificationStatus());
    if (ok) toast.success('Push notifications enabled');
    else if (Notification.permission === 'denied') toast.error('Notifications blocked — enable them in your browser settings');
    else toast.error('Could not enable push notifications');
  };

  const disablePush = async () => {
    await unsubscribePush();
    setPermission(pushNotificationStatus());
    toast.success('Push notifications disabled on this device');
  };

  if (loading || !prefs) {
    return (
      <Section title="Notifications">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
          <div className="loading-spinner" />
        </div>
      </Section>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Section title="Browser Push">
        <p style={{ color: '#8892b0', fontSize: '0.9rem', marginBottom: 16 }}>
          {permission === 'unsupported' && 'Your browser doesn\'t support push notifications.'}
          {permission === 'granted' && 'Push notifications are enabled on this device. You can disable them here, or in your browser settings.'}
          {permission === 'denied' && 'Notifications are blocked for this site. Enable them in your browser settings, then come back here.'}
          {permission === 'default' && 'Get notified about likes, comments, messages, and events even when this tab is closed.'}
        </p>
        {permission === 'granted' ? (
          <button onClick={disablePush} className="btn-secondary">Disable on this device</button>
        ) : permission === 'default' ? (
          <button onClick={enablePush} className="btn-primary">Enable Push Notifications</button>
        ) : null}
      </Section>

      {NOTIF_SECTIONS.map(section => (
        <Section title={section.title} key={section.title}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {section.items.map(item => (
              <Toggle
                key={item.key}
                checked={!!prefs[item.key]}
                onChange={() => togglePref(item.key)}
                label={item.label}
                description={item.description}
              />
            ))}
          </div>
        </Section>
      ))}
    </div>
  );
};

// ─── Blocked Users (App Store UGC compliance — guideline 1.2) ────────────────
// Mirror of the mobile screen. Lets the user review who they've blocked and
// undo it. Apple reviewers expect blocking to be a managed, reversible action.
const BlockedUsersTab = () => {
  const [blocks, setBlocks] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    api.get('/moderation/blocks')
      .then(({ data }) => { setBlocks(data.blocks || []); setError(false); })
      .catch(() => { setBlocks([]); setError(true); });
  };

  useEffect(() => { load(); }, []);

  const unblock = async (b) => {
    if (!window.confirm(`Unblock @${b.user?.username}? You'll start seeing their content again.`)) return;
    try {
      await api.delete(`/moderation/blocks/${b.user.id}`);
      setBlocks(prev => (prev || []).filter(x => x.id !== b.id));
      toast.success(`Unblocked @${b.user?.username}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not unblock.');
    }
  };

  return (
    <Section title="Blocked Users">
      <p style={{ color: '#8892b0', fontSize: '0.875rem', marginBottom: 20, lineHeight: 1.6 }}>
        People you've blocked won't see your profile or content, and you won't see theirs.
        Use the ••• menu on any post, comment, or profile to block or report a user — blocked
        users vanish from your feed instantly.
      </p>

      {blocks === null ? (
        <p style={{ color: '#5a657a', fontSize: '0.85rem' }}>Loading…</p>
      ) : blocks.length === 0 ? (
        <p style={{ color: '#5a657a', fontSize: '0.85rem', fontStyle: 'italic' }}>
          {error ? 'Could not load blocked users.' : "You haven't blocked anyone."}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {blocks.map(b => (
            <li
              key={b.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 14px',
                background: '#0f131c', border: '1px solid #2a3142',
              }}
            >
              {b.user?.profileImageUrl
                ? <img src={b.user.profileImageUrl} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid #3a445c' }} />
                : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1a2030', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c9ced6', fontWeight: 700, border: '1px solid #3a445c' }}>{(b.user?.username?.[0] || '?').toUpperCase()}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#f0f1f5', fontWeight: 600, fontSize: '0.92rem' }}>@{b.user?.username || 'user'}</div>
                <div style={{ color: '#5a657a', fontSize: '0.78rem', marginTop: 2 }}>
                  Blocked {new Date(b.createdAt).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => unblock(b)}
                style={{
                  padding: '7px 14px', borderRadius: 999,
                  background: 'transparent', color: '#e6e9f0',
                  border: '1px solid #3a445c', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: 600,
                }}
              >
                Unblock
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

// ─── Terms of Use tab (in-app copy — also at /terms) ─────────────────────────
const TermsTab = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <Section title="Terms of Use">
      <p style={{ color: '#5a657a', fontSize: '0.8rem', marginBottom: 6 }}>Last updated: 12 May 2026</p>
      <p style={{ color: '#c8cfe0', fontSize: '0.9rem', lineHeight: 1.65, marginBottom: 12 }}>
        By creating an account, signing in, or otherwise using MyCarCollection you agree to
        be bound by these Terms of Use. If you do not agree, you must not use the Service.
      </p>

      <h4 style={{ color: '#fff', fontSize: '0.95rem', marginTop: 14, marginBottom: 6 }}>
        Zero tolerance for objectionable content and abusive users
      </h4>
      <p style={{ color: '#c8cfe0', fontSize: '0.9rem', lineHeight: 1.65, marginBottom: 6 }}>
        MyCarCollection has zero tolerance for objectionable content and abusive behaviour.
        You agree that you will not post, upload, share, or otherwise make available any
        content that is, or that a reasonable person would consider to be:
      </p>
      <ul style={{ paddingLeft: 22, color: '#c8cfe0', fontSize: '0.9rem', lineHeight: 1.55 }}>
        <li>unlawful, threatening, defamatory, obscene, or pornographic;</li>
        <li>harassing, bullying, abusive, hateful, or discriminatory;</li>
        <li>depicting or encouraging violence, self-harm, animal cruelty, or sexual exploitation;</li>
        <li>promoting illegal activity, including the sale of stolen vehicles or parts;</li>
        <li>infringing the intellectual property, privacy, or other rights of any person;</li>
        <li>impersonating another person or misrepresenting your affiliation with anyone;</li>
        <li>spam, unsolicited advertising, scams, or repetitive low-quality content.</li>
      </ul>
      <p style={{ color: '#c8cfe0', fontSize: '0.9rem', lineHeight: 1.65, marginTop: 10 }}>
        We reserve the right to remove any content, and to suspend or permanently terminate
        any account, that violates these rules, in our sole discretion and without notice.
        Repeated or severe violations will result in a permanent ban.
      </p>

      <h4 style={{ color: '#fff', fontSize: '0.95rem', marginTop: 18, marginBottom: 6 }}>
        Reporting and blocking
      </h4>
      <p style={{ color: '#c8cfe0', fontSize: '0.9rem', lineHeight: 1.65 }}>
        Every post, comment, vehicle, review, message, event, and user profile can be
        reported via the ••• menu. You can block any user from their profile or content;
        once blocked, their content disappears from your feed immediately and they can no
        longer interact with you. Reports are reviewed and actioned within 24 hours.
      </p>

      <h4 style={{ color: '#fff', fontSize: '0.95rem', marginTop: 18, marginBottom: 6 }}>
        Contact
      </h4>
      <p style={{ color: '#c8cfe0', fontSize: '0.9rem', lineHeight: 1.65 }}>
        Questions, complaints, or notice of objectionable content can be sent to{' '}
        <a href="mailto:support@mycarcollection.app" style={{ color: '#4a9eff' }}>
          support@mycarcollection.app
        </a>
        . We respond to reports within 24 hours.
      </p>

      <p style={{ color: '#5a657a', fontSize: '0.8rem', marginTop: 20 }}>
        Want the full version?{' '}
        <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#4a9eff', textDecoration: 'underline' }}>
          Open the standalone Terms page
        </a>
        .
      </p>
    </Section>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

// ─── Username ─────────────────────────────────────────────────────────────────
// Changing a username is not like changing a bio: it releases the old handle for
// anyone else to register, and every link, bookmark and marketplace listing that
// pointed at the old one keeps pointing at the name rather than at this account.
// So it gets its own section, checks availability while you type, states the
// consequence, and confirms before committing.
//
// Its own <form>, deliberately NOT inside the profile tab's form: that one
// submits to saveProfile, and a nested form is invalid HTML anyway. Its own
// state too — Settings' `form` object seeds from `user` once and never re-syncs,
// so a username folded into it would show a stale handle after a rename.
const UsernameSection = ({ user, dispatch }) => {
  const [value, setValue] = useState(user?.username || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // null = not checked yet; otherwise { available, reason }
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);

  // Guards against a slow early response overwriting a fast later one — type
  // fast enough and the checks come back out of order, which would otherwise
  // leave the field claiming the wrong verdict for the text now in it.
  const checkSeq = useRef(0);
  const debounceRef = useRef(null);

  const trimmed = value.trim();
  // Three states, not two. `sameIdentity` frees nothing and can steal nothing,
  // so the server allows it during a cooldown and without spending one — but
  // only a `caseOnly` change is worth submitting; an exact match is a no-op.
  const sameIdentity = isSameUsername(trimmed, user?.username);
  const noop = trimmed === (user?.username || '');
  const caseOnly = sameIdentity && !noop;
  // Never applied to a handle the user already holds: accounts grandfathered
  // under the older, looser rules would otherwise be told the name they are
  // wearing is invalid, with no way to restyle it. Mirrors the server.
  const formatError = sameIdentity ? null : validateUsernameFormat(trimmed);

  // The server sends the deadline, already resolved against its own clock and
  // its own policy — the client never computes the cooldown length.
  //
  // Only the PARSE is memoised. Comparing against the clock inside the memo
  // pinned the verdict for the whole mount, so a cooldown that expired while
  // this screen was open stayed locked, above a message naming a date already
  // in the past. Compared at render instead, so any re-render clears it. There
  // is deliberately no timer: the window is 30 days, and the field stays
  // editable throughout, so the keystroke that starts the next rename is itself
  // the re-render that unlocks the button.
  const readyAt = useMemo(() => {
    if (!user?.usernameChangeAvailableAt) return null;
    const d = new Date(user.usernameChangeAvailableAt);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [user?.usernameChangeAvailableAt]);
  const cooldown = readyAt && readyAt.getTime() > Date.now() ? readyAt : null;

  // Re-seed if the stored username changes underneath us (another device, or a
  // fetchMe refresh) so the field never argues with the account.
  useEffect(() => { setValue(user?.username || ''); }, [user?.username]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setAvailability(null);
    // Bumped BEFORE the early return, not after: a request already in flight
    // has to be invalidated even when the new text needs no check of its own,
    // or its verdict lands on text it was never about.
    const seq = ++checkSeq.current;
    if (sameIdentity || formatError) { setChecking(false); return; }

    setChecking(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/auth/username/available', { params: { username: trimmed } });
        if (seq === checkSeq.current) setAvailability(data);
      } catch {
        // A failed check is not a failed rename — say nothing and let the
        // server decide on submit rather than blocking on a network blip.
        if (seq === checkSeq.current) setAvailability(null);
      } finally {
        if (seq === checkSeq.current) setChecking(false);
      }
    }, 450);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [trimmed, sameIdentity, formatError]);

  const commit = async () => {
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put('/auth/username', { username: trimmed });
      // Everything past this line is post-commit: the rename has already
      // happened on the server and cannot be undone, so nothing here may fall
      // into the catch below and tell the user it failed. Persisting the
      // reissued token is a convenience, not a requirement — the old token
      // still authenticates (auth resolves the row by id) and /auth/me hands
      // out a fresh one on the next load.
      if (data.token) {
        try { localStorage.setItem('token', data.token); }
        catch (e) { /* private mode / quota — the old token still works */ }
      }
      dispatch(updateUser(data.user));
      toast.success(`You're now @${data.user.username}`);
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      // A rejection is also NEW INFORMATION about this account, and the server
      // ships it structured for exactly that reason. Without folding it back
      // into the state that gates this field, a client whose cached user
      // predates a rename made elsewhere stays unlocked: the tick stays green,
      // the button re-enables, and every retry re-shows a confirmation dialog
      // promising to release a handle the server will not release.
      if (status === 429 && data?.availableAt) {
        dispatch(updateUser({ usernameChangeAvailableAt: data.availableAt }));
      } else if (status === 409) {
        // Won from under us between the availability check and the submit.
        setAvailability({ available: false, reason: data?.message });
      }
      // Surface the server's message verbatim — it carries the specific reason
      // (taken, reserved, cooldown) that a generic string would throw away.
      setError(data?.message || 'Could not change your username.');
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (formatError) { setError(formatError); return; }
    if (noop) return;
    setError('');
    // Restyling your own capitalisation releases nothing, so it does not get
    // the warning — or the confirmation step — that giving up a handle does.
    if (caseOnly) { commit(); return; }
    const ok = window.confirm(
      `Change your username to @${trimmed}?\n\n`
      + `Your old username @${user?.username} is released and someone else can take it — `
      + `links and mentions pointing at it will no longer reach you.\n\n`
      + `You can only change this once every ${RENAME_COOLDOWN_DAYS} days.`,
    );
    if (ok) commit();
  };

  // One status line under the field, so its height never jumps.
  let status;
  if (error) status = { text: error, color: '#e84545' };
  // Announced ahead of everything else, because it is the reason the button is
  // dead — but not while the pending edit is a case-only restyle, which the
  // cooldown does not gate.
  else if (cooldown && !caseOnly) status = { text: `You can change your username again on ${cooldown.toLocaleDateString()}.`, color: '#8892b0' };
  else if (caseOnly) status = { text: 'Changes how your username is capitalised.', color: '#8892b0' };
  else if (sameIdentity) status = { text: USERNAME_HINT, color: '#8892b0' };
  else if (formatError) status = { text: formatError, color: '#e84545' };
  else if (checking) status = { text: 'Checking availability…', color: '#8892b0' };
  else if (availability?.available) status = { text: `@${trimmed} is available`, color: '#4caf7d' };
  else if (availability) status = { text: availability.reason || 'That username is not available.', color: '#e84545' };
  else status = { text: USERNAME_HINT, color: '#8892b0' };

  // `noop`, not `sameIdentity`: a case-only restyle is a real, submittable
  // change. The cooldown does not apply to it either — it releases nothing —
  // which is exactly the rule setUsername enforces server-side.
  const blocked = saving || noop || !!formatError
    || (!!cooldown && !caseOnly)
    || availability?.available === false;

  return (
    <form onSubmit={onSubmit}>
      <Section title="Username">
        <div className="form-group">
          <div style={socialWrapStyle}>
            <span style={socialPrefixStyle}>@</span>
            <input
              value={value}
              onChange={(e) => { setValue(sanitiseUsernameInput(e.target.value)); if (error) setError(''); }}
              placeholder="yourname"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={USERNAME_MAX}
              disabled={saving}
              aria-label="Username"
              aria-invalid={status.color === '#e84545'}
              style={socialInputStyle}
            />
            {!sameIdentity && (checking || availability) && (
              <span style={{ paddingRight: 12, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                {checking ? <SpinnerIcon /> : availability?.available ? <CheckIcon /> : <CrossIcon />}
              </span>
            )}
          </div>
          <div style={{ color: status.color, fontSize: '0.82rem', marginTop: 8 }} role="status">{status.text}</div>
        </div>
        <div style={{ color: '#8892b0', fontSize: '0.82rem', marginBottom: 16, lineHeight: 1.5 }}>
          Your username is how people find and mention you. Changing it releases your old one,
          and you can only change it once every {RENAME_COOLDOWN_DAYS} days.
        </div>
        <button type="submit" className="btn-primary" disabled={blocked}>
          {saving ? 'Saving...' : 'Save Username'}
        </button>
      </Section>
    </form>
  );
};

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(s => s.auth);
  const [tab, setTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [followRequests, setFollowRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    bio: user?.bio || '',
    location: user?.location || '',
    state: user?.state || '',
    instagramUrl: socialHandle('instagram', user?.instagramUrl),
    facebookUrl: socialHandle('facebook', user?.facebookUrl),
    tiktokUrl: socialHandle('tiktok', user?.tiktokUrl),
    garageValueVisible: user?.garageValueVisible ?? true,
    isPrivate: user?.isPrivate ?? false,
  });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const handlePwd = e => setPasswords(p => ({ ...p, [e.target.name]: e.target.value }));
  const toggle = (key) => setForm(f => ({ ...f, [key]: !f[key] }));

  useEffect(() => {
    if (tab !== 'requests') return;
    setRequestsLoading(true);
    api.get('/users/me/follow-requests')
      .then(({ data }) => setFollowRequests(data.requests))
      .catch(() => {})
      .finally(() => setRequestsLoading(false));
  }, [tab]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put(`/users/${user.id}`, {
        ...form,
        instagramUrl: buildSocialUrl('instagram', form.instagramUrl) || '',
        facebookUrl: buildSocialUrl('facebook', form.facebookUrl) || '',
        tiktokUrl: buildSocialUrl('tiktok', form.tiktokUrl) || '',
      });
      dispatch(updateUser(data.user));
      toast.success('Profile updated!');
    } catch { toast.error('Failed to update profile'); }
    finally { setLoading(false); }
  };

  const savePrivacy = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.put(`/users/${user.id}`, {
        garageValueVisible: form.garageValueVisible,
        isPrivate: form.isPrivate,
      });
      dispatch(updateUser(data.user));
      toast.success('Privacy settings saved');
    } catch { toast.error('Failed to save settings'); }
    finally { setLoading(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.put('/auth/change-password', { currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      toast.success('Password changed!');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to change password'); }
    finally { setLoading(false); }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const upload = await downscaleImage(file);
    const fd = new FormData();
    fd.append('avatar', upload);
    try {
      const { data } = await api.post(`/users/${user.id}/avatar`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      dispatch(updateUser({ profileImageUrl: data.profileImageUrl }));
      toast.success('Avatar updated!');
    } catch (err) {
      console.error('[avatar] upload failed:', err.response?.status, err.response?.data);
      toast.error(err.response?.data?.message || 'Failed to upload avatar');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== user.username) { toast.error('Username does not match'); return; }
    setDeleteLoading(true);
    try {
      await api.delete(`/users/${user.id}`);
      dispatch(logout());
      navigate('/');
      toast.success('Account deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete account');
    } finally { setDeleteLoading(false); }
  };

  const acceptRequest = async (req) => {
    try {
      await api.post(`/users/me/follow-requests/${req.id}/accept`);
      setFollowRequests(prev => prev.filter(r => r.id !== req.id));
      toast.success(`Accepted ${req.user.username}`);
    } catch { toast.error('Failed to accept request'); }
  };

  const declineRequest = async (req) => {
    try {
      await api.delete(`/users/me/follow-requests/${req.id}`);
      setFollowRequests(prev => prev.filter(r => r.id !== req.id));
    } catch { toast.error('Failed to decline request'); }
  };

  const TABS = [
    { key: 'profile', label: 'Profile' },
    { key: 'privacy', label: 'Privacy' },
    { key: 'requests', label: 'Follow Requests', badge: true },
    { key: 'notifications', label: 'Notifications' },
    { key: 'password', label: 'Password' },
    { key: 'blocked', label: 'Blocked Users' },
    { key: 'account', label: 'Account' },
    { key: 'feedback', label: 'Report / Feedback' },
    { key: 'terms', label: 'Terms of Use' },
  ];

  return (
    <div className="container page" style={{ maxWidth: 1000 }}>
      <h1 style={{ marginBottom: 32 }}>Settings</h1>

      <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
        {/* Left sidebar */}
        <nav style={{
          width: 200,
          flexShrink: 0,
          borderRight: '1px solid #2a3142',
          paddingRight: 0,
          position: 'sticky',
          top: 80,
        }}>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '10px 20px',
                background: tab === t.key ? 'rgba(255,255,255,0.05)' : 'none',
                border: 'none',
                borderRight: tab === t.key ? '2px solid #4a9eff' : '2px solid transparent',
                borderRadius: 0,
                color: tab === t.key ? '#f0f1f5' : '#8892b0',
                fontSize: '0.875rem',
                fontWeight: tab === t.key ? 600 : 400,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
              }}
            >
              {t.label}
              {t.badge && followRequests.length > 0 && tab !== 'requests' && (
                <span style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: '#e84545', color: '#fff',
                  fontSize: '0.65rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {followRequests.length}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0, paddingLeft: 32 }}>

      {tab === 'profile' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <UsernameSection user={user} dispatch={dispatch} />
        <form onSubmit={saveProfile} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section title="Profile Photo">
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              {user?.profileImageUrl
                ? <img src={user.profileImageUrl} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid #3a445c' }} />
                : <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#1a2030', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', fontWeight: 800, color: '#c9ced6', border: '1px solid #3a445c' }}>{user?.username?.[0]?.toUpperCase()}</div>}
              <label className="btn-secondary" style={{ cursor: 'pointer' }}>
                Change Photo
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
              </label>
            </div>
          </Section>

          <Section title="Personal Info">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group"><label>First Name</label><input className="input" name="firstName" value={form.firstName} onChange={handle} /></div>
              <div className="form-group"><label>Last Name</label><input className="input" name="lastName" value={form.lastName} onChange={handle} /></div>
            </div>
            <div className="form-group"><label>Bio</label><textarea className="input" name="bio" value={form.bio} onChange={handle} rows={3} placeholder="Tell the community about yourself and your passion for cars..." /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group"><label>Location</label><input className="input" name="location" value={form.location} onChange={handle} placeholder="Sydney, NSW" /></div>
              <div className="form-group"><label>State</label>
                <select className="input" name="state" value={form.state} onChange={handle}>
                  <option value="">Select state</option>
                  {AU_STATES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Changes'}</button>
          </Section>

          <Section title="Social Links">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group"><label>Instagram</label>
                <div style={socialWrapStyle}>
                  <span style={socialPrefixStyle}>{SOCIAL_PREFIX.instagram}</span>
                  <input name="instagramUrl" value={form.instagramUrl} onChange={handle} placeholder="yourhandle" autoComplete="off" style={socialInputStyle} />
                </div>
              </div>
              <div className="form-group"><label>TikTok</label>
                <div style={socialWrapStyle}>
                  <span style={socialPrefixStyle}>{SOCIAL_PREFIX.tiktok}</span>
                  <input name="tiktokUrl" value={form.tiktokUrl} onChange={handle} placeholder="yourhandle" autoComplete="off" style={socialInputStyle} />
                </div>
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label>Facebook</label>
                <div style={socialWrapStyle}>
                  <span style={socialPrefixStyle}>{SOCIAL_PREFIX.facebook}</span>
                  <input name="facebookUrl" value={form.facebookUrl} onChange={handle} placeholder="yourhandle" autoComplete="off" style={socialInputStyle} />
                </div>
              </div>
            </div>
            <div style={{ color: '#8892b0', fontSize: '0.82rem', marginBottom: 16 }}>These will appear as clickable links on your profile.</div>
            <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Links'}</button>
          </Section>
        </form>
        </div>
      )}

      {tab === 'privacy' && (
        <form onSubmit={savePrivacy} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section title="Account Visibility">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <Toggle
                checked={form.isPrivate}
                onChange={() => toggle('isPrivate')}
                label="Private Account"
                description="When enabled, only approved followers can see your posts and garage. New followers will need to send a request that you approve."
              />
            </div>
            <div style={{ marginTop: 24 }}>
              <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save Privacy Settings'}</button>
            </div>
          </Section>
        </form>
      )}

      {tab === 'requests' && (
        <Section title="Follow Requests">
          {requestsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <div className="loading-spinner" />
            </div>
          ) : followRequests.length === 0 ? (
            <p style={{ color: '#5a657a', fontSize: '0.9rem', margin: 0 }}>No pending follow requests.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {followRequests.map(req => (
                <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: '1px solid #2a3142' }}>
                  {req.user.profileImageUrl
                    ? <img src={req.user.profileImageUrl} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '1px solid #3a445c', flexShrink: 0 }} />
                    : <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#1a2030', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#c9ced6', border: '1px solid #3a445c', flexShrink: 0 }}>{req.user.username?.[0]?.toUpperCase()}</div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{req.user.username}</div>
                    {(req.user.firstName || req.user.lastName) && (
                      <div style={{ color: '#8892b0', fontSize: '0.8rem' }}>{[req.user.firstName, req.user.lastName].filter(Boolean).join(' ')}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button className="btn-primary btn-sm" onClick={() => acceptRequest(req)}>Accept</button>
                    <button className="btn-secondary btn-sm" onClick={() => declineRequest(req)}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {tab === 'password' && (
        <form onSubmit={savePassword}>
          <Section title="Change Password">
            <div className="form-group"><label>Current Password</label><input className="input" type="password" name="currentPassword" value={passwords.currentPassword} onChange={handlePwd} required /></div>
            <div className="form-group"><label>New Password</label><input className="input" type="password" name="newPassword" value={passwords.newPassword} onChange={handlePwd} required minLength={8} /></div>
            <div className="form-group"><label>Confirm New Password</label><input className="input" type="password" name="confirmPassword" value={passwords.confirmPassword} onChange={handlePwd} required /></div>
            <button type="submit" className="btn-primary" disabled={loading}>{loading ? 'Updating...' : 'Change Password'}</button>
          </Section>
        </form>
      )}

      {tab === 'notifications' && <NotificationsTab />}

      {tab === 'feedback' && <FeedbackTab />}

      {tab === 'blocked' && <BlockedUsersTab />}

      {tab === 'terms' && <TermsTab />}

      {tab === 'account' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Section title="Sign Out">
            <p style={{ color: '#8892b0', marginBottom: 20, fontSize: '0.9rem' }}>Sign out of your account on this device.</p>
            <button
              onClick={async () => {
                await unsubscribePush();
                dispatch(logout());
                navigate('/login');
              }}
              className="btn-primary"
            >
              Sign Out
            </button>
          </Section>

          <Section title="Delete Account" danger>
            <p style={{ color: '#8892b0', marginBottom: 20, fontSize: '0.9rem' }}>
              This will permanently delete your account, garage, vehicles, and all associated data. This cannot be undone.
            </p>
            <div className="form-group">
              <label>Type your username <span style={{ color: '#c9ced6', fontWeight: 700 }}>@{user?.username}</span> to confirm</label>
              <input className="input" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder={user?.username} autoComplete="off" />
            </div>
            <button
              onClick={handleDeleteAccount}
              disabled={deleteLoading || deleteConfirm !== user?.username}
              style={{ background: deleteConfirm === user?.username ? '#c0392b' : '#2a1a1a', color: '#fff', border: '1px solid #c0392b', padding: '10px 24px', cursor: deleteConfirm === user?.username ? 'pointer' : 'not-allowed', fontSize: '0.9rem', fontWeight: 600, opacity: deleteLoading ? 0.6 : 1 }}
            >
              {deleteLoading ? 'Deleting...' : 'Permanently Delete Account'}
            </button>
          </Section>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
